/*
 * Pince-moi : « Compose ton duo / ton coffret » (sections/pm-bundle-builder.liquid).
 * Une pince à choisir par étape ; le prix se met à jour ; un seul bouton ajoute tout au panier
 * et prévient le tiroir panier via l'événement standard CartLinesUpdateEvent.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const routes = window.Theme?.routes ?? {};
const ADD_URL = routes.cart_add_url || '/cart/add.js';
const CART_URL = (routes.cart_url || '/cart') + '.js';

function formatMoney(cents) {
  const currency = window.Shopify?.currency?.active || 'EUR';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(cents / 100);
}

class PmBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.percent = Number(this.dataset.percent) || 0;
    try {
      this.labels = JSON.parse(this.querySelector('template[data-labels]')?.innerHTML || '{}');
    } catch {
      this.labels = {};
    }
    this.steps = [...this.querySelectorAll('[data-step]')];
    this.addButton = this.querySelector('[data-add]');

    this.addEventListener('click', (event) => {
      const edit = event.target.closest('[data-edit]');
      if (edit && this.contains(edit)) {
        edit.closest('[data-step]').classList.add('is-open');
        return;
      }
      const chip = event.target.closest('.pm-bundle__chip');
      if (chip && this.contains(chip)) return this.filter(chip);
      const option = event.target.closest('.pm-bundle__option');
      if (option && this.contains(option)) this.select(option);
    });
    this.addButton?.addEventListener('click', () => this.add());

    // ?pince=<handle> : présélectionne la première pince
    const handle = new URLSearchParams(location.search).get('pince');
    if (handle && this.steps[0]) {
      const option = this.steps[0].querySelector(`.pm-bundle__option[data-handle="${CSS.escape(handle)}"]`);
      if (option) this.select(option, false);
    }
    this.update();
  }

  // Filtre les articles d'une étape par catégorie (data-cat), « Tout » = data-filter vide
  filter(chip) {
    const step = chip.closest('[data-step]');
    const cat = chip.dataset.filter;
    step.querySelectorAll('.pm-bundle__chip').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
    step.querySelectorAll('.pm-bundle__options > li').forEach((li) => {
      li.hidden = Boolean(cat) && li.dataset.cat !== cat;
    });
  }

  select(option, advance = true) {
    const step = option.closest('[data-step]');
    step.querySelectorAll('.pm-bundle__option').forEach((o) => o.setAttribute('aria-pressed', String(o === option)));
    step.dataset.selected = 'true';
    step.classList.remove('is-open');
    const edit = step.querySelector('[data-edit]');
    if (edit) edit.hidden = false;
    const choice = step.querySelector('[data-choice]');
    if (choice) choice.textContent = option.dataset.name;
    this.update();

    // Fait défiler vers le choix suivant s'il n'est pas encore fait
    if (advance) {
      const next = this.steps.find((s) => s.dataset.selected !== 'true');
      next?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  get selection() {
    return this.steps.map((s) => s.querySelector('.pm-bundle__option[aria-pressed="true"]'));
  }

  update() {
    const chosen = this.selection.filter(Boolean);
    const complete = chosen.length === this.steps.length;
    const compare = chosen.reduce((sum, o) => sum + Number(o.dataset.price), 0);
    const total = Math.round((compare * (100 - this.percent)) / 100);

    const totalEl = this.querySelector('[data-total]');
    const compareEl = this.querySelector('[data-compare]');
    const saveEl = this.querySelector('[data-save]');

    if (totalEl) totalEl.textContent = chosen.length ? formatMoney(complete ? total : compare) : '—';
    if (compareEl) compareEl.textContent = complete && this.percent ? formatMoney(compare) : '';
    if (saveEl) {
      saveEl.hidden = !(complete && this.percent);
      saveEl.textContent = `${this.labels.save || 'Vous économisez'} ${formatMoney(compare - total)}`;
    }

    if (this.addButton) {
      this.addButton.disabled = !complete;
      const label = this.addButton.querySelector('[data-add-label]');
      if (label) label.textContent = complete ? `${this.labels.add} · ${formatMoney(total)}` : this.labels.waiting;
    }
  }

  async add() {
    const chosen = this.selection;
    if (chosen.some((o) => !o)) return;

    const counts = new Map();
    chosen.forEach((o) => counts.set(o.dataset.variantId, (counts.get(o.dataset.variantId) || 0) + 1));
    const items = [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const status = this.querySelector('[data-status]');
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      }).then((r) => r.json());
      const cart = await fetch(CART_URL, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const didError = Boolean(response.status);

      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (status) status.textContent = response.description || this.labels.error;
      } else if (status) {
        status.textContent = this.labels.added;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'pm-bundle-builder',
          itemCount: chosen.length,
          sections: didError ? undefined : response.sections,
          didError,
        },
      });
    } catch (error) {
      deferred.reject(error);
      if (status) status.textContent = this.labels.error;
    } finally {
      this.addButton.removeAttribute('aria-busy');
      this.update();
    }
  }
}

if (!customElements.get('pm-bundle-builder')) {
  customElements.define('pm-bundle-builder', PmBundleBuilder);
}
